// src/components/Registration/NfpRegistration/Step3NfpLocations.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useNfpReg } from '../../../contexts/NfpRegContext';
import { nfpApi } from '../../../services/nfpApi';

const PEACH = '#EBA477';
const GREEN = '#10B981';
const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');
const BROAD_GEOGRAPHY_TYPES = new Set([
  'locality',
  'postal_code',
  'administrative_area_level_1',
  'administrative_area_level_2',
  'administrative_area_level_3',
  'administrative_area_level_4',
  'country',
  'neighborhood',
  'sublocality',
  'route',
]);

/* ---------------- helpers ---------------- */
function pickComp(components = [], types = [], short = false) {
  const hit = components.find((c) => types.every((t) => c.types?.includes(t)));
  return hit ? (short ? hit.short_name : hit.long_name) : '';
}

function parsePlaceAddress(details = {}) {
  const comps = details.address_components || [];
  const street = [pickComp(comps, ['street_number']), pickComp(comps, ['route'])]
    .filter(Boolean)
    .join(' ');
  const city =
    pickComp(comps, ['locality']) ||
    pickComp(comps, ['sublocality']) ||
    pickComp(comps, ['administrative_area_level_3']);
  const state = pickComp(comps, ['administrative_area_level_1'], true);
  const zip = pickComp(comps, ['postal_code']);
  const country = pickComp(comps, ['country'], true) || 'USA';
  // fallback from formatted address if any piece missing
  if ((!street || !city || !state || !zip) && (details.formatted_address || details.formattedAddress)) {
    const fa = (details.formatted_address || details.formattedAddress).split(',');
    const guessStreet = fa[0]?.trim();
    const guessCity = fa[1]?.trim();
    const stateZip = (fa[2] || '').trim().split(/\s+/);
    const guessState = stateZip[0] || '';
    const guessZip = stateZip[1] || '';
    return {
      street: street || guessStreet || '',
      city: city || guessCity || '',
      state: state || guessState || '',
      zip: zip || guessZip || '',
      country,
    };
  }
  return { street, city, state, zip, country };
}

function formatAddressText(address = {}) {
  return [address.street, address.city, address.state, address.zip].filter(Boolean).join(', ');
}

function buildSearchSignature(name, areas = []) {
  return JSON.stringify({
    name: String(name || '').trim().toLowerCase(),
    areas: [...areas].map((area) => String(area || '').trim().toLowerCase()).filter(Boolean).sort(),
  });
}

function normalizeLooseText(value = '') {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
}

function normalizeAreaValue(value = '') {
  const cleaned = String(value || '').trim().replace(/\s+/g, ' ').replace(/,+$/, '');
  if (!cleaned) return '';
  const parts = cleaned.split(',').map((part) => part.trim()).filter(Boolean);
  if (parts.length === 2 && /^[a-z]{2}$/i.test(parts[1])) {
    return `${parts[0]}, ${parts[1].toUpperCase()}`;
  }
  return parts.join(', ');
}

function isLikelyStreetAddress(value = '') {
  const normalized = String(value || '').trim().toLowerCase();
  const commaParts = normalized.split(',').map((part) => part.trim()).filter(Boolean);
  if (commaParts.length >= 3) return true;
  if (/^\d+\s+\S+/.test(normalized)) return true;
  if (
    /\b(?:street|avenue|road|boulevard|drive|lane|court|circle|highway|parkway|trail|terrace|suite|unit|floor|apartment|building|bldg|po box)\b/i.test(
      normalized
    )
  ) {
    return true;
  }
  return false;
}

function isAreaOnlyCandidate(result = {}) {
  const primaryType = String(result.primaryType || '').trim().toLowerCase();
  const types = Array.isArray(result.types)
    ? result.types.map((type) => String(type || '').trim().toLowerCase()).filter(Boolean)
    : [];
  const hasBroadType =
    BROAD_GEOGRAPHY_TYPES.has(primaryType) ||
    types.some((type) => BROAD_GEOGRAPHY_TYPES.has(type));

  if (!hasBroadType) {
    return false;
  }

  const areaNorm = normalizeLooseText(result._area);
  const nameNorm = normalizeLooseText(result.name);
  const addressLeadNorm = normalizeLooseText(String(result.formattedAddress || '').split(',')[0]);

  if (!areaNorm) {
    return true;
  }

  return nameNorm === areaNorm || addressLeadNorm === areaNorm;
}

function ensureSingleHq(locations = []) {
  let foundHq = false;
  return locations.map((loc) => {
    const shouldKeepHq = !!loc?.isHq && !foundHq;
    if (shouldKeepHq) foundHq = true;
    return { ...loc, isHq: shouldKeepHq };
  });
}

function locationToHqDraft(loc = {}) {
  if (!loc || typeof loc !== 'object') return null;
  const address = loc.address || {};
  return {
    name: loc.name || 'HQ',
    website: loc.website || '',
    address: {
      street: address.street || '',
      city: address.city || '',
      state: address.state || '',
      zip: address.zip || '',
      country: address.country || 'USA',
    },
    placeId: loc.placeId || '',
    addressText: loc.addressText || formatAddressText(address),
    marketKey: loc.marketKey || '',
  };
}

/* ---------------- small UI atoms ---------------- */
const FrostedInput = (props) => (
  <input
    {...props}
    className="frosted-input"
    style={{
      width: '100%',
      height: 52,
      borderRadius: 14,
      border: '1px solid rgba(255,255,255,0.10)',
      background: 'rgba(255,255,255,0.12)',
      color: 'white',
      fontSize: 16,
      padding: '0 14px',
      outline: 'none',
      boxSizing: 'border-box',
      ...(props.style || {}),
    }}
  />
);

const SmallButton = ({ children, style, ...rest }) => (
  <button
    {...rest}
    style={{
      background: 'rgba(255,255,255,0.12)',
      border: '1px solid rgba(255,255,255,0.18)',
      color: 'white',
      borderRadius: 12,
      height: 32,
      padding: '0 12px',
      fontWeight: 700,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      ...style,
    }}
  >
    {children}
  </button>
);

const PrimaryButton = ({ children, style, ...rest }) => (
  <button
    {...rest}
    style={{
      border: 'none',
      borderRadius: 12,
      minWidth: 110,
      height: 32,
      padding: '0 14px',
      fontWeight: 800,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: PEACH,
      color: '#08141d',
      ...style,
    }}
  >
    {children}
  </button>
);

const pillStyle = {
  border: '1px solid rgba(255,255,255,0.25)',
  padding: '2px 8px',
  borderRadius: 10,
  background: 'rgba(255,255,255,0.06)',
  fontSize: 12,
};

const inlineLinkStyle = {
  color: '#9DD9FF',
  textDecoration: 'none',
};

/* ===================== Component ===================== */
export default function Step3NfpLocations({ onBack, onNext }) {
  const { draft, setDraft, registration, setRegistration } = useNfpReg();

  const entityPublicName = draft?.entity?.publicName?.trim() || '';
  const entityLegalName = draft?.entity?.legalName?.trim() || '';

  // Prefer public (DBA) for search; fall back to legal or historical identity
  const defaultSearchName =
    entityPublicName ||
    draft?.identity?.publicName?.trim() ||
    entityLegalName ||
    draft?.identity?.legalName?.trim() ||
    '';

  // Areas served
  const [serviceAreas, setServiceAreas] = useState(
    Array.isArray(draft?.locations?.serviceAreas) ? draft.locations.serviceAreas : []
  );
  const [areaInput, setAreaInput] = useState('');
  const [activeArea, setActiveArea] = useState((draft?.locations?.activeArea || serviceAreas[0]) || '');

  // Physical footprint
  const [hasPhysical, setHasPhysical] = useState(draft?.locations?.hasPhysicalLocations ? 'yes' : 'no');

  // Search (public/DBA)
  const [searchName, setSearchName] = useState(draft?.locations?.searchName || defaultSearchName);
  const [results, setResults] = useState([]);
  const [isSearching, setSearching] = useState(false);
  const [error, setError] = useState('');
  const [lastSearchSignature, setLastSearchSignature] = useState(() =>
    Array.isArray(draft?.locations?.locations) && draft.locations.locations.length > 0
      ? buildSearchSignature(draft?.locations?.searchName || defaultSearchName, draft?.locations?.serviceAreas || [])
      : ''
  );

  // Selected locations
  const [locations, setLocations] = useState(() =>
    ensureSingleHq(draft?.locations?.locations || [])
  );

  // HQ (for no-physical orgs)
  const [hqAddr, setHqAddr] = useState(
    draft?.locations?.hq?.address || { street: '', city: '', state: '', zip: '', country: 'USA' }
  );
  const [hqWebsite, setHqWebsite] = useState(draft?.locations?.hq?.website || (draft?.entity?.website || ''));
  const searchSignature = useMemo(
    () => buildSearchSignature(searchName, serviceAreas),
    [searchName, serviceAreas]
  );
  const canSearchLocations = hasPhysical === 'yes' && !!searchName.trim() && serviceAreas.length > 0;
  const searchNeedsRefine = !!lastSearchSignature && lastSearchSignature !== searchSignature;

  const buildLocalPayload = (overrides = {}) => ({
    serviceAreas: overrides.serviceAreas ?? serviceAreas,
    activeArea: overrides.activeArea ?? activeArea,
    hasPhysicalLocations: hasPhysical === 'yes',
    searchName,
    locations,
    hq:
      hasPhysical === 'yes'
        ? undefined
        : {
            website: hqWebsite || '',
            address: {
              street: hqAddr.street || '',
              city: hqAddr.city || '',
              state: hqAddr.state || '',
              zip: hqAddr.zip || '',
              country: hqAddr.country || 'USA',
            },
            placeId: draft?.locations?.hq?.placeId || '',
          },
  });

  const buildApiPayload = (payload) => ({
    areasServed: payload.serviceAreas,
    hasPhysicalLocations: payload.hasPhysicalLocations,
    locationDrafts: payload.hasPhysicalLocations
      ? payload.locations.map((loc) => ({
          placeId: loc.placeId || '',
          name: loc.name || '',
          addressText: loc.addressText || formatAddressText(loc.address),
          address: loc.address || {},
          phone: loc.phone || '',
          website: loc.website || '',
          marketKey: loc.marketKey || '',
          status: loc.status || 'active',
          isHq: !!loc.isHq,
        }))
      : [],
    hqDraft:
      payload.hasPhysicalLocations
        ? locationToHqDraft(payload.locations.find((loc) => loc?.isHq))
        : !payload.hq
        ? undefined
        : {
            name: draft?.entity?.legalName || 'HQ',
            website: payload.hq.website || '',
            address: payload.hq.address || {},
            placeId: payload.hq.placeId || '',
          },
  });

  const persistStepState = async ({ remote = true, overrides } = {}) => {
    const payload = buildLocalPayload(overrides);
    setDraft((prev) => ({ ...prev, locations: payload }));

    if (!remote || !registration?._id) return { payload };

    const resp = await nfpApi.putLocations(registration._id, buildApiPayload(payload));
    if (resp?.registration) setRegistration(resp.registration);
    return { payload, resp };
  };

  /* ---------- areas served helpers ---------- */
  const removeArea = (value) => {
    const next = (serviceAreas || []).filter((x) => x !== value);
    setServiceAreas(next);
    if (activeArea === value) setActiveArea(next[0] || '');
  };

  const commitPendingAreaInput = () => {
    const pending = normalizeAreaValue(areaInput);
    if (!pending) {
      return { added: false, serviceAreas, activeArea };
    }
    if (isLikelyStreetAddress(pending)) {
      setError('Enter a city, county, ZIP code, or state only. Do not enter a full street address.');
      return { added: false, serviceAreas, activeArea };
    }
    setError('');
    const nextServiceAreas = Array.from(new Set([...(serviceAreas || []), pending])).slice(0, 20);
    const nextActiveArea = activeArea || nextServiceAreas[0] || '';
    setServiceAreas(nextServiceAreas);
    if (!activeArea && nextServiceAreas.length) setActiveArea(nextServiceAreas[0]);
    setAreaInput('');
    return { added: true, serviceAreas: nextServiceAreas, activeArea: nextActiveArea };
  };

  /* ---------- backend places search (scoped to areas) ---------- */
  const searchPlaces = async ({ signatureOverride } = {}) => {
    setError('');
    setResults([]);

    if (hasPhysical !== 'yes') {
      setError('Select “Yes” to search for physical locations.');
      return;
    }
    const trimmedManual = searchName.trim();
    const nameCandidates = trimmedManual ? [trimmedManual] : [];
    if (!nameCandidates.length) {
      setError('Enter a legal or public-facing name to search.');
      return;
    }
    if (!serviceAreas.length) {
      setError('Add at least one “Area served” to scope the search.');
      return;
    }

    setSearching(true);
    try {
      const targets = [...serviceAreas];
      const all = [];

      for (const area of targets) {
        for (const name of nameCandidates) {
          const params = new URLSearchParams({ name, city: area });
          const res = await fetch(`${API_BASE}/api/places/search?${params.toString()}`, { credentials: 'include' });
          const json = await res.json();
          if (!res.ok) throw new Error(json?.error || json?.message || 'Search failed');
          const list = (json.candidates || json.results || json.places || []).map((r) => ({
            placeId: r.place_id || r.placeId,
            name: r.displayName || r.name,
            formattedAddress: r.formatted_address || r.formattedAddress || r.vicinity || r.address,
            primaryType: r.primaryType || '',
            types: Array.isArray(r.types) ? r.types : [],
            iconMaskBaseUri: r.iconMaskBaseUri || '',
            iconBackgroundColor: r.iconBackgroundColor || '',
            businessStatus: r.businessStatus || '',
            _area: area,
            _searchedName: name,
          }));
          all.push(...list);
        }
      }

      // Deduplicate and hide already-added
      const seen = new Set();
      const enrolled = new Set((locations || []).map((l) => l.placeId).filter(Boolean));
      const deduped = all.filter((r) => {
        if (!r.placeId) return false;
        if (seen.has(r.placeId) || enrolled.has(r.placeId)) return false;
        if (isAreaOnlyCandidate(r)) return false;
        seen.add(r.placeId);
        return true;
      });

      setResults(deduped);
      setLastSearchSignature(signatureOverride || searchSignature);
    } catch (e) {
      setError(e.message || 'Search failed');
    } finally {
      setSearching(false);
    }
  };

  const addByDetails = async (placeId, areaForThis) => {
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/places/details?placeId=${encodeURIComponent(placeId)}`, {
        credentials: 'include',
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d?.error || d?.message || 'Details failed');

      const loc = {
        placeId: d.place_id || d.placeId || placeId,
        placeSource: 'google',
        name: d.name,
        addressText: d.formatted_address || d.formattedAddress,
        address: parsePlaceAddress(d),
        phone: d.international_phone_number || d.formatted_phone_number || d.phone || '',
        website: d.website || '',
        hoursText: Array.isArray(d?.opening_hours?.weekday_text) ? d.opening_hours.weekday_text : [],
        primaryType: (Array.isArray(d.types) && d.types[0]) || d.primaryType || '',
        iconMaskBaseUri: d.iconMaskBaseUri || '',
        iconBackgroundColor: d.iconBackgroundColor || '',
        businessStatus: d.business_status || d.businessStatus || '',
        status: 'active',
        exclusionReason: '',
        reopenDate: '',
        marketKey: areaForThis || activeArea || '',
        isHq: false,
      };

      setLocations((prev) => {
        return [...ensureSingleHq(prev), loc];
      });
      setResults((prev) => prev.filter((r) => r.placeId !== placeId));
    } catch (e) {
      setError(e.message || 'Failed to add location');
    }
  };

  const removeLocation = (idx) => {
    setLocations((prev) => prev.filter((_, i) => i !== idx));
  };

  const markLocationAsHq = (idx) => {
    setLocations((prev) =>
      prev.map((loc, locIdx) => ({
        ...loc,
        isHq: locIdx === idx,
      }))
    );
  };

  useEffect(() => {
    if (!canSearchLocations) return;
    if (lastSearchSignature) return;
    searchPlaces({ signatureOverride: searchSignature });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSearchLocations, lastSearchSignature, searchSignature]);

  /* ---------- persist + nav ---------- */
  const handleNext = async () => {
    if (areaInput.trim()) {
      setError('Click Add Area to save the entry you typed, or clear the field before continuing.');
      return;
    }
    const effectiveServiceAreas = serviceAreas;
    const effectiveActiveArea = activeArea;

    if (hasPhysical === 'yes' && locations.length === 0) {
      setError('Add at least one physical location.');
      return;
    }
    if (hasPhysical === 'yes' && !effectiveServiceAreas.length && !locations.some((loc) => loc?.isHq)) {
      setError('Add at least one “Area served” or mark one physical location as HQ.');
      return;
    }
    if (hasPhysical !== 'yes') {
      if (!hqAddr.city || !hqAddr.state) {
        setError('Provide HQ city and state.');
        return;
      }
    }

    try {
      await persistStepState({
        remote: true,
        overrides: { serviceAreas: effectiveServiceAreas, activeArea: effectiveActiveArea },
      });
    } catch (apiErr) {
      setError(apiErr?.message || 'Failed to save locations');
      return;
    }

    onNext();
  };

  const handleBack = async () => {
    if (areaInput.trim()) {
      setError('Click Add Area to save the entry you typed, or clear the field before leaving this step.');
      return;
    }
    try {
      await persistStepState({
        remote: true,
        overrides: { serviceAreas, activeArea },
      });
    } catch {
      await persistStepState({
        remote: false,
        overrides: { serviceAreas, activeArea },
      });
    }
    onBack();
  };

  /* ---------------- render ---------------- */
  return (
    <div style={{ display: 'grid', gap: 24 }}>
      {/* Areas served */}
      <section style={{ display: 'grid', gap: 12 }}>
        <h3 style={{ margin: 0 }}>Areas served</h3>
        <div style={{ fontSize: 14, opacity: 0.86, lineHeight: 1.45 }}>
          Add cities as City, ST and counties as County, ST so we can match the right location. You can also
          add ZIP codes or states.
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 10, alignItems: 'end' }}>
          <FrostedInput
            placeholder="Enter a city, ZIP code, county, or state"
            value={areaInput}
            autoComplete="off"
            onChange={(e) => {
              setAreaInput(e.target.value);
              if (error) setError('');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && areaInput.trim()) {
                e.preventDefault();
                commitPendingAreaInput();
              }
            }}
          />
          <SmallButton
            type="button"
            onClick={commitPendingAreaInput}
            disabled={!areaInput.trim()}
            style={{
              minWidth: 108,
              height: 52,
              background: areaInput.trim() ? PEACH : 'rgba(255,255,255,0.12)',
              border: `1px solid ${areaInput.trim() ? PEACH : 'rgba(255,255,255,0.18)'}`,
              color: areaInput.trim() ? '#08141d' : 'rgba(255,255,255,0.75)',
              fontWeight: 800,
            }}
          >
            Add Area
          </SmallButton>
        </div>
        <div style={{ fontSize: 12, opacity: 0.78 }}>
          Add one area at a time, then click Add. City entries can include state, like Lake Forest, IL. At
          least one area served is recommended; a verified HQ/root market can also support local discovery.
        </div>
        {serviceAreas.length > 0 && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {serviceAreas.map((a) => (
              <span
                key={a}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '4px 10px',
                  borderRadius: 999,
                  background: 'rgba(255,255,255,0.12)',
                  border: '1px solid rgba(255,255,255,0.18)',
                  fontSize: 13,
                  ...(activeArea === a ? { outline: `1px solid ${PEACH}` } : {}),
                }}
              >
                {a}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeArea(a);
                  }}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: 'rgba(255,255,255,0.85)',
                    cursor: 'pointer',
                  }}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
      </section>

      {/* Physical footprint */}
      <section style={{ display: 'grid', gap: 12 }}>
        <h3 style={{ margin: 0 }}>Does your organization have any physical locations?</h3>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => setHasPhysical('yes')}
            style={{
              minWidth: 120,
              height: 48,
              borderRadius: 14,
              border: `1px solid ${hasPhysical === 'yes' ? PEACH : 'rgba(255,255,255,0.18)'}`,
              background: hasPhysical === 'yes' ? PEACH : 'rgba(255,255,255,0.08)',
              color: hasPhysical === 'yes' ? '#08141d' : 'white',
              fontWeight: 800,
              cursor: 'pointer',
            }}
          >
            Yes
          </button>
          <button
            type="button"
            onClick={() => setHasPhysical('no')}
            style={{
              minWidth: 120,
              height: 48,
              borderRadius: 14,
              border: `1px solid ${hasPhysical === 'no' ? PEACH : 'rgba(255,255,255,0.18)'}`,
              background: hasPhysical === 'no' ? PEACH : 'rgba(255,255,255,0.08)',
              color: hasPhysical === 'no' ? '#08141d' : 'white',
              fontWeight: 800,
              cursor: 'pointer',
            }}
          >
            No
          </button>
        </div>

        {error && <div style={{ color: '#ffdada' }}>{error}</div>}
      </section>

      {/* Physical locations */}
      {hasPhysical === 'yes' && (
        <section
          style={{
            borderRadius: 14,
            border: '1px solid rgba(255,255,255,0.10)',
            background: 'rgba(255,255,255,0.06)',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              padding: '10px 14px',
              borderBottom: '1px solid rgba(255,255,255,0.10)',
              fontWeight: 600,
            }}
          >
            Find your physical locations
          </div>

          <div style={{ display: 'grid', gap: 12, padding: '12px 14px' }}>
            <div style={{ fontSize: 14, opacity: 0.86, lineHeight: 1.45 }}>
              We’ll use your organization name and areas served to find likely locations. Select the locations
              that belong to your organization, and mark one as HQ if applicable.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 10, alignItems: 'end' }}>
              <div>
                <label style={{ display: 'block', marginBottom: 6, opacity: 0.9 }}>Public / DBA name</label>
                <FrostedInput
                  placeholder="e.g., Boys & Girls Club of Lake County"
                  value={searchName}
                  onChange={(e) => setSearchName(e.target.value)}
                />
              </div>
              <SmallButton
                type="button"
                onClick={() => searchPlaces({ signatureOverride: searchSignature })}
                disabled={isSearching || !canSearchLocations || (!searchNeedsRefine && !!lastSearchSignature)}
                style={{
                  minWidth: 132,
                  height: 52,
                  background: isSearching ? 'rgba(255,255,255,0.18)' : GREEN,
                  border: `1px solid ${isSearching ? 'rgba(255,255,255,0.25)' : GREEN}`,
                  color: '#fff',
                  opacity: (!searchNeedsRefine && !!lastSearchSignature) ? 0.6 : 1,
                }}
              >
                {isSearching ? 'Searching…' : 'Refine Search'}
              </SmallButton>
            </div>
            {!canSearchLocations && (
              <div style={{ fontSize: 12, opacity: 0.8 }}>
                Add at least one area served to help us find matching locations.
              </div>
            )}
          </div>
        </section>
      )}

      {/* Candidates (when physical locations) */}
      {hasPhysical === 'yes' && results.length > 0 && (
        <section
          style={{
            borderRadius: 14,
            border: '1px solid rgba(255,255,255,0.10)',
            background: 'rgba(255,255,255,0.06)',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              padding: '10px 14px',
              borderBottom: '1px solid rgba(255,255,255,0.10)',
              fontWeight: 600,
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <span>Likely locations across your added areas</span>
            <span style={{ opacity: 0.85 }}>{results.length}</span>
          </div>

          {results.map((r) => (
            <div
              key={r.placeId}
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr auto',
                gap: 12,
                padding: '12px 14px',
                borderTop: '1px solid rgba(255,255,255,0.08)',
              }}
            >
              <div>
                <div style={{ fontWeight: 650 }}>{r.name}</div>
                <div style={{ opacity: 0.85 }}>{r.formattedAddress}</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
                  {r.primaryType && <span style={pillStyle}>{r.primaryType}</span>}
                  {r.businessStatus && <span style={pillStyle}>Status: {r.businessStatus}</span>}
                  {r._area && <span style={pillStyle}>Area: {r._area}</span>}
                </div>
              </div>

                <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifySelf: 'flex-end', flexWrap: 'wrap' }}>
                <PrimaryButton
                  type="button"
                  onClick={() => addByDetails(r.placeId, r._area)}
                  style={{ width: 128 }}
                >
                  Select location
                </PrimaryButton>
              </div>
            </div>
          ))}
        </section>
      )}

      {/* Selected locations */}
      {locations.length > 0 && (
        <section
          style={{
            borderRadius: 14,
            border: '1px solid rgba(255,255,255,0.10)',
            background: 'rgba(255,255,255,0.06)',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              padding: '10px 14px',
              borderBottom: '1px solid rgba(255,255,255,0.10)',
              fontWeight: 600,
            }}
          >
            Added locations ({locations.length})
          </div>

          {locations.map((l, idx) => (
            <div
              key={`${l.placeId || 'manual'}-${idx}`}
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr auto',
                gap: 12,
                padding: '12px 14px',
                borderTop: '1px solid rgba(255,255,255,0.08)',
              }}
            >
              <div>
                <div style={{ fontWeight: 650 }}>
                  {l.name || '—'} <span style={{ opacity: 0.85, fontWeight: 400 }}>• {l.marketKey || 'Area'}</span>
                  {l.isHq && <span style={{ ...pillStyle, marginLeft: 8, borderColor: PEACH, color: PEACH }}>HQ</span>}
                </div>
                {l.addressText && <div style={{ opacity: 0.9 }}>{l.addressText}</div>}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
                  {l.primaryType && <span style={pillStyle}>{l.primaryType}</span>}
                  {l.businessStatus && <span style={pillStyle}>Status: {l.businessStatus}</span>}
                </div>
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 8, fontSize: 14 }}>
                  {l.phone && (
                    <a href={`tel:${l.phone}`} style={inlineLinkStyle}>
                      {l.phone}
                    </a>
                  )}
                  {l.website && (
                    <a href={l.website} target="_blank" rel="noreferrer" style={inlineLinkStyle}>
                      {(() => {
                        try {
                          return new URL(l.website).hostname;
                        } catch {
                          return l.website;
                        }
                      })()}
                    </a>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifySelf: 'flex-end' }}>
                {!l.isHq && (
                  <SmallButton type="button" onClick={() => markLocationAsHq(idx)}>
                    Mark as HQ
                  </SmallButton>
                )}
                <SmallButton style={{ minWidth: 110 }} type="button" onClick={() => removeLocation(idx)}>
                  Remove
                </SmallButton>
              </div>
            </div>
          ))}
        </section>
      )}

      {/* Verification details when no physical footprint */}
      {hasPhysical === 'no' && (
        <section
          style={{
            borderRadius: 14,
            border: '1px solid rgba(255,255,255,0.10)',
            background: 'rgba(255,255,255,0.06)',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              padding: '10px 14px',
              borderBottom: '1px solid rgba(255,255,255,0.10)',
              fontWeight: 600,
            }}
          >
            Organization verification details
          </div>
          <div style={{ display: 'grid', gap: 12, padding: '12px 14px' }}>
            <div style={{ fontSize: 14, opacity: 0.86, lineHeight: 1.45 }}>
              Provide the details we can use to verify your organization. This information is for verification
              only and does not create a physical location in rypil.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div>
                <label style={{ display: 'block', marginBottom: 6, opacity: 0.9 }}>City</label>
                <FrostedInput
                  value={hqAddr.city}
                  onChange={(e) => setHqAddr({ ...hqAddr, city: e.target.value })}
                  placeholder="Chicago"
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: 6, opacity: 0.9 }}>State</label>
                <FrostedInput
                  value={hqAddr.state}
                  onChange={(e) => setHqAddr({ ...hqAddr, state: e.target.value })}
                  placeholder="IL"
                />
              </div>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 6, opacity: 0.9 }}>Street / Mailing Address (optional)</label>
              <FrostedInput
                value={hqAddr.street}
                onChange={(e) => setHqAddr({ ...hqAddr, street: e.target.value })}
                placeholder="123 Main St"
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 6, opacity: 0.9 }}>Website (optional)</label>
              <FrostedInput
                value={hqWebsite}
                onChange={(e) => setHqWebsite(e.target.value)}
                placeholder="https://example.org"
              />
            </div>
          </div>
        </section>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <button type="button" className="secondary" onClick={handleBack}>
          Back
        </button>
        <button type="button" className="primary" onClick={handleNext}>
          Continue
        </button>
      </div>

      <style>{`.frosted-input::placeholder{color:rgba(255,255,255,0.75);}`}</style>
    </div>
  );
}
